import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000AutoCompleteInput from "../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U3129ProjectSearchPanel } from "../../../u3129Timesheet/components/form/U3129LineProjectSubprojectRowCells";
import type { U3129FindProjectItem } from "../../../u3129Timesheet/U3129Service";
import { NS } from "../../U3139";
import { useU3139FindProjectHandlers } from "../../U3139UseFindProjectHandlers";

type AddDlgFormRow = {
  project: { id?: string; projetNumber?: string; projetName?: string } | null;
};

const emptyAddDlgForm = (): AddDlgFormRow => ({ project: null });

export interface U3139MinutesAddDlgProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (projetNumber: string) => void | Promise<void>;
}

const U3139MinutesAddDlg: React.FC<U3139MinutesAddDlgProps> = ({ isOpen, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [formState, formStateDispatch] = useReducer<U2000FormState, [action: { type: string; data?: unknown }]>(u2000FormState, u2000IntFormState);
  const findProjectHandlers = useU3139FindProjectHandlers();

  const formStateHandlers = useU2000FormStateHandlers<AddDlgFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: {
      newRecord: () => emptyAddDlgForm(),
      validateForm: (row) => {
        const result: Record<string, string> = {};
        if (!row.project?.projetNumber?.trim()) {
          result.project = t("errProjectRequired", { ns: NS });
        }
        return result;
      },
      saveForme: async () => {},
    },
  });

  const projetNumber = formStateHandlers.getData()?.project?.projetNumber?.trim() ?? "";

  useEffect(() => {
    if (!isOpen) return;
    setSubmitting(false);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    formStateHandlers.setData(emptyAddDlgForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onSearchProject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => findProjectHandlers.search(criterias),
    [findProjectHandlers],
  );

  const extractProject = useCallback((result: U3129FindProjectItem | null): { fieldName: string; value: unknown }[] => {
    if (result) {
      return [{ fieldName: "project", value: { id: result.id, projetNumber: result.projetNumber, projetName: result.projetName } }];
    }
    return [{ fieldName: "project", value: null }];
  }, []);

  if (!isOpen) return null;

  const handleSave = async () => {
    const num = projetNumber;
    if (!num || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(num);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDialogClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      onClose();
      return;
    }
    void handleSave();
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={handleDialogClose}
      title={t("lblAddMinuteDlgTitle", { ns: NS })}
      isValid={Boolean(projetNumber) && !submitting}
      width="max-w-lg"
    >
      <div className="flex flex-col gap-4 p-1">
        <U2000AutoCompleteInput
          pageSize={15}
          formStateHandlers={formStateHandlers as any}
          fieldName="project"
          mainFieldName="projetNumber"
          entityLink="U3121"
          label={t("lblProject", { ns: NS })}
          onSearch={onSearchProject}
          displayFn={(item) => {
            const p = item as U3129FindProjectItem | null;
            if (!p) return "";
            const num = (p.projetNumber ?? p.id ?? "").trim();
            const name = p.projetName?.trim() ?? "";
            if (num && name) return `${num} — ${name}`;
            return num || name;
          }}
          overrideExtractDataFn={(item) => extractProject(item as U3129FindProjectItem | null)}
          basicResultHeight={300}
          resultLabels={[]}
          resultFields={["projetNumber", "projetName"]}
          minCarLaunchSearch={3}
          customSearchPanelHeight={500}
          panelWidth={400}
          dropdownWidth={400}
          renderPanel={(props) => <U3129ProjectSearchPanel {...props} ns="u3129" />}
          readonly={submitting}
          required
        />
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U3139MinutesAddDlg;
